import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

import { GlobalOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Spin } from 'antd';

import type { FieldProps } from 'formik';
import { useField } from 'formik';
import { Hint } from 'v2/components/Shared/Hint';
import useMask from 'v2/hooks/useMask';
import type { TestResult } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

type UrlValidationFormFieldProps = FieldProps & {
  mask?: string;
  variant?: 'outlined' | 'borderless' | 'filled';
  label?: string;
  helperText?: React.ReactNode;
  onValidateUrl: (valid: boolean, url?: string) => void;
  resetValidation?: number;
  helpLink?: string;
  validateUrlCallback?: (url: string, type: string, contentType: string) => Promise<TestResult>;
  placeholder?: string;
  disabled?: boolean;
};

const UrlValidationFormField = ({
  field,
  form,
  helperText,
  helpLink = 'https://eduzz.github.io/webhook-v2',
  validateUrlCallback = webhookService.validateUrl,
  resetValidation,
  mask,
  onValidateUrl,
  label: _label,
  variant: _variant,
  ...props
}: UrlValidationFormFieldProps) => {
  const name = field.name;
  const [, meta] = useField(name);

  const [result, setResult] = React.useState<TestResult>();
  const [validating, setValidating] = React.useState(false);
  const value = field?.value || '';
  const { maskedValue, maskClean } = useMask(mask, value);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setResult(null);
      onValidateUrl(false);
      return form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [onValidateUrl, form, field.name, maskClean]
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;

      form.setFieldTouched(field.name, true);

      if (!v) return;

      form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  const validUrl = useCallback(async () => {
    try {
      setValidating(true);
      const values = form.values;
      const result = await validateUrlCallback(values.url, values.type, values.content_type);
      setValidating(false);
      setResult(result);
      onValidateUrl(result.valid, values.url);
    } catch (e) {
      console.log(e);
      toast.error('Ocorreu um erro ao validar a url');
      setValidating(false);
    }
  }, [onValidateUrl, validateUrlCallback, form]);

  const errorText = meta.touched && (meta.error || null);
  const showResult = !!result?.requestData;
  const urlSuccess = showResult && result?.valid;
  const urlFailed = showResult && !result?.valid;
  const isUrlFormatValid = !errorText;

  useEffect(() => {
    if (resetValidation) {
      setResult(null);
      onValidateUrl(false);
    }
  }, [resetValidation, onValidateUrl]);

  return (
    <>
      <Hint inputName={field.name}>
        <div className='flex! w-full! flex-wrap items-center! gap-3'>
          <Input
            className='grow'
            status={errorText ? 'error' : undefined}
            name={name}
            value={maskedValue}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            {...props}
          />

          {(errorText || helperText) && (
            <p className={`mt-1 mb-0! text-xs ${errorText ? 'text-red-500' : 'text-gray-400'}`}>
              {errorText || helperText}
            </p>
          )}

          <Button
            type='primary'
            onClick={validUrl}
            icon={validating ? <Spin size='small' /> : <ThunderboltOutlined style={{ fontSize: 20 }} />}
            disabled={!isUrlFormatValid || !field.value || validating}
          >
            Validar URL
          </Button>
        </div>
      </Hint>

      {showResult && (
        <div className='pt-2.5'>
          {urlSuccess && <Alert type='success' message='URL verificada com sucesso!' className='text-sm shadow-none' />}

          {urlFailed && (
            <Alert
              type='error'
              className='text-sm shadow-none'
              message={
                <>
                  <p className='mb-0!'>Tentativa de envio teste para url falhou!</p>
                  <p className='mb-0!'>Verifique os detalhes abaixo:</p>
                </>
              }
              action={
                <a href={helpLink} target='_blank' rel='noreferrer'>
                  <Button>Ajuda</Button>
                </a>
              }
            />
          )}

          <div className='pt-2.5'>
            <p>Dados enviados para o servidor (URL)</p>
            <div className='mt-3 overflow-auto rounded-sm bg-[#f3f3f3] p-3 wrap-break-word [hyphens:auto] outline-1 outline-[#ccc]'>
              <p
                style={{
                  marginBottom: 0,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: '100%'
                }}
              >
                <b>POST</b> <br />
                <span>{value}</span>
              </p>
            </div>
          </div>

          <div className='pt-2.5'>
            <p className='mb-1!'>Resposta do servidor (URL)</p>
            <div className='mt-3 overflow-auto rounded-sm bg-[#f3f3f3] p-3 wrap-break-word [hyphens:auto] outline-1 outline-[#ccc]'>
              <a
                target='_blank'
                href={`https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Status/${result?.response.status}`}
                rel='noreferrer'
              >
                <div className='mb-3 flex'>
                  <GlobalOutlined style={{ fontSize: 24 }} />
                  <span className='ml-1.5'>
                    Status: {result?.response.status} {result?.response.statusText}
                  </span>
                </div>
              </a>
              <p className='mt-1! mb-0!'>{result?.response.helperText}</p>
              <p style={{ marginTop: 16, marginBottom: 0 }}>
                <strong>Tempo de resposta:</strong> {`${result?.response.duration || 0} ms`}
              </p>
            </div>
          </div>

          {!urlSuccess && (
            <div className='pt-2.5'>
              <p>Corpo da resposta</p>
              <pre className='mt-3 overflow-auto rounded-sm bg-[#f3f3f3] p-3 wrap-break-word [hyphens:auto] outline-1 outline-[#ccc]'>
                {result?.response?.data ?? '-'}
              </pre>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default UrlValidationFormField;
